/**
 * ServiceCatalog Page
 * Public landing page for widget - no authentication required
 * Displays available services with material types and allows users to start order flow
 */

import { useNavigate } from 'react-router-dom';
import { useProviderConfig } from '../../contexts/ProviderConfigContext';
import { ServiceCard } from '../../components/catalog/ServiceCard';
import { ServiceCardSkeleton } from '../../components/widget/ServiceCardSkeleton';
import { PackageIcon, Clock, CheckCircle } from 'lucide-react';
import { useRetry } from '../../hooks/useRetry';
import { getErrorMessage } from '../../utils/errorMessages';
import { ErrorAlert } from '../../components/widget/ErrorAlert';
import { useLoadingDelay } from '../../hooks/useLoadingDelay';
import { Skeleton } from '../../components/common/Skeleton';

// Common material types grouped for easy browsing
const POPULAR_MATERIALS = [
  { value: 'mixed_cd', label: 'Construction & Demolition', icon: '🏗️' },
  { value: 'green_waste', label: 'Green Waste (Yard Debris)', icon: '🌿' },
  { value: 'concrete', label: 'Concrete', icon: '🧱' },
  { value: 'household', label: 'Household Junk', icon: '🏠' },
];

export function ServiceCatalog() {
  const navigate = useNavigate();
  const { config, isLoading, error, refetch } = useProviderConfig();
  const { retry, isRetrying } = useRetry();

  // Use loading delay to prevent flash for fast loads
  const showSkeleton = useLoadingDelay(isLoading, 200);

  const handleSelectService = (containerSize: string) => {
    // Navigate to order flow with pre-selected container size
    navigate('order', {
      state: { containerSize },
    });
  };

  // Loading state with skeleton loaders - only show after delay
  if (showSkeleton) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header skeleton */}
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Skeleton className="h-12 w-32" />
                <Skeleton className="h-8 w-48" />
              </div>
              <Skeleton className="h-6 w-16" />
            </div>
          </div>
        </header>

        {/* Hero skeleton */}
        <section className="bg-gradient-to-b from-primary-50 to-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
            <div className="text-center max-w-3xl mx-auto">
              <Skeleton className="h-12 w-3/4 mx-auto mb-6" />
              <Skeleton className="h-6 w-full mb-2" />
              <Skeleton className="h-6 w-2/3 mx-auto mb-8" />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex flex-col items-center">
                    <Skeleton className="h-12 w-12 rounded-full mb-3" />
                    <Skeleton className="h-5 w-24 mb-1" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services grid skeleton */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Skeleton className="h-8 w-48 mx-auto mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <ServiceCardSkeleton key={i} isPopular={i === 1} />
            ))}
          </div>
        </section>
      </div>
    );
  }

  // Error state - Show error with retry option
  if (error || (!config && !isLoading)) {
    const errorMessage = error
      ? getErrorMessage(new Error(error))
      : { message: 'Service configuration is not available.', suggestion: 'Please try again later.' };

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Services</h2>
            <p className="text-gray-600">We could not load the service catalog.</p>
          </div>

          <ErrorAlert
            error={errorMessage}
            onRetry={() => retry(refetch)}
            isRetrying={isRetrying}
            dismissible={false}
          />
        </div>
      </div>
    );
  }

  // TypeScript guard - config is guaranteed to be non-null here
  if (!config) {
    return null;
  }

  // Determine most popular service (default to 20Y if available)
  const popularSize = '20Y';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip Navigation Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:border-2 focus:border-primary focus:rounded focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Header with provider branding */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              {config.logoUrl && (
                <img
                  src={config.logoUrl}
                  alt={`${config.companyName} logo`}
                  className="h-8 sm:h-12 w-auto flex-shrink-0"
                />
              )}
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{config.companyName}</h1>
            </div>

            {/* Login link */}
            <a
              href="../login"
              className="inline-flex items-center justify-center min-h-[44px] min-w-[64px] px-4 py-2 text-sm font-medium text-primary hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md whitespace-nowrap flex-shrink-0"
            >
              Sign In
            </a>
          </div>
        </div>
      </header>

      {/* Hero section */}
      <section className="bg-gradient-to-b from-primary-50 to-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Need a Dumpster? Get Started in Minutes
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
              Choose your container size, schedule delivery, and we will take care of the rest.
              Fast, simple, and professional service you can count on.
            </p>

            {/* Key benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-10">
              <div className="flex flex-col items-center p-4 sm:p-0">
                <div className="bg-white rounded-full p-3 shadow-md mb-3">
                  <Clock className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Quick Delivery</h3>
                <p className="text-xs sm:text-sm text-gray-600">Same or next-day service available</p>
              </div>
              <div className="flex flex-col items-center p-4 sm:p-0">
                <div className="bg-white rounded-full p-3 shadow-md mb-3">
                  <PackageIcon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Multiple Sizes</h3>
                <p className="text-xs sm:text-sm text-gray-600">10 to 40 yard containers</p>
              </div>
              <div className="flex flex-col items-center p-4 sm:p-0">
                <div className="bg-white rounded-full p-3 shadow-md mb-3">
                  <CheckCircle className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Transparent Pricing</h3>
                <p className="text-xs sm:text-sm text-gray-600">No hidden fees or surprises</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular materials section */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
            What are you disposing?
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {POPULAR_MATERIALS.map((material) => (
              <button
                key={material.value}
                onClick={() => navigate('order', { state: { preselectedMaterial: material.value } })}
                className="flex flex-col items-center justify-center min-h-[100px] sm:min-h-[88px] p-4 bg-gray-50 hover:bg-primary-50 border-2 border-gray-200 hover:border-primary-500 rounded-lg transition-all duration-200 group active:scale-95 touch-manipulation"
              >
                <span className="text-3xl mb-2">{material.icon}</span>
                <span className="text-sm font-medium text-gray-900 text-center group-hover:text-primary-700">
                  {material.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
          Available Services
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" style={{ gridAutoRows: 'minmax(min-content, max-content)' }}>
          {config.services.map((service) => {
            const rentalFee = config.pricing.basePricing?.[service.containerSize];
            const pricingDisplay = rentalFee ? `$${rentalFee}` : 'Contact for pricing';

            return (
              <ServiceCard
                key={service.containerSize}
                service={service}
                pricing={pricingDisplay}
                onSelect={() => handleSelectService(service.containerSize)}
                isPopular={service.containerSize === popularSize}
              />
            );
          })}
        </div>

        {/* No services available message */}
        {config.services.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No services currently available.</p>
          </div>
        )}
      </section>

      {/* Contact information section */}
      <section className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Have Questions? We Are Here to Help
            </h3>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6">
              {/* Phone */}
              <a
                href={`tel:${config.contactPhone}`}
                className="inline-flex items-center min-h-[48px] px-4 py-3 text-gray-700 hover:text-primary transition-colors rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <svg
                  className="w-5 h-5 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span className="font-medium">{config.contactPhone}</span>
              </a>

              {/* Email */}
              <a
                href={`mailto:${config.contactEmail}`}
                className="inline-flex items-center min-h-[48px] px-4 py-3 text-gray-700 hover:text-primary transition-colors rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <svg
                  className="w-5 h-5 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="font-medium">{config.contactEmail}</span>
              </a>

              {/* Website (if available) */}
              {config.website && (
                <a
                  href={config.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center min-h-[48px] px-4 py-3 text-gray-700 hover:text-primary transition-colors rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <svg
                    className="w-5 h-5 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                    />
                  </svg>
                  <span className="font-medium">Visit Website</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
